<template>
  <!-- eslint-disable tailwindcss/no-custom-classname -->
  <div class="grid grid-rows-1 gap-4 md:grid-cols-2 md:gap-8">
    <RevSkeleton
      v-if="pending"
      class="col-span-2"
      height="100px"
      shape="rectangle"
      width="100%"
    />

    <RevSkeleton
      v-if="pending && isAITagsExperimentEnabled"
      class="col-span-2"
      height="100px"
      shape="rectangle"
      width="100%"
    />

    <RevSkeleton
      v-if="pending"
      class="col-start-2 md:hidden"
      height="24px"
      shape="rectangle"
      width="100%"
    />

    <template v-if="!pending">
      <AIText v-if="aiSummary?.summary?.text" :text="aiSummary.summary.text" />

      <AITags v-if="isAITagsExperimentEnabled" class="mt-16" />

      <span
        v-if="aiSummary?.summary?.updatedAt"
        class="caption-grid-item text-static-default-low flex shrink-0 items-center justify-end gap-4 py-6"
      >
        {{ i18n(translations.caption) }}

        <RevTooltip>
          <template #trigger="{ show, hide }">
            <IconInfo
              class="inline"
              size="16"
              @focusin="show"
              @focusout="hide"
              @mouseleave="hide"
              @mouseover="show"
            />
          </template>

          {{ tooltipText }}
        </RevTooltip>
      </span>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import type { ReviewsAISummary } from '@backmarket/nuxt-layer-reviews/api/reviews/types'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevSkeleton } from '@ds/components/Skeleton'
import { RevTooltip } from '@ds/components/Tooltip'
import { IconInfo } from '@ds/icons/IconInfo'

import AITags from '../AITags/AITags.vue'
import AIText from '../AIText/AIText.vue'

import translations from './AISummary.translations'

const i18n = useI18n()
const experiments = useExperiments()

const props = defineProps<{
  aiSummary: ReviewsAISummary | undefined | null
  pending: boolean
}>()

const AI_SUMMARY_REVIEW_COUNT_THRESHOLD = 1000

const isAITagsExperimentEnabled =
  experiments['experiment.reviewsAiTags'] === 'withAITags'

const formattedUpdatedAt = computed(() => {
  if (!props.aiSummary?.summary?.updatedAt) return ''

  return i18n.date(new Date(props.aiSummary.summary.updatedAt))
})

const tooltipText = computed(() => {
  if (!props.aiSummary?.summary?.reviewsCount) return ''
  if (
    props.aiSummary.summary.reviewsCount > AI_SUMMARY_REVIEW_COUNT_THRESHOLD
  ) {
    return i18n(translations.tooltip, {
      updateDate: formattedUpdatedAt.value,
    })
  }
  if (
    props.aiSummary?.summary?.reviewsCount === AI_SUMMARY_REVIEW_COUNT_THRESHOLD
  ) {
    return i18n(translations.tooltipLatestReviews, {
      number: AI_SUMMARY_REVIEW_COUNT_THRESHOLD,
      updateDate: formattedUpdatedAt.value,
    })
  }

  return i18n(translations.tooltipAllReviews, {
    updateDate: formattedUpdatedAt.value,
  })
})
</script>

<style scoped>
.caption-grid-item {
  @media screen and (min-width: theme('screens.md')) {
    grid-column: 2;
    grid-row: 1;
  }
}
</style>
